import React from 'react';
import { Transaction } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount, formatDateShort, formatExactPrice } from '../../utils/formatters';
import { CoinIcon } from './CoinIcon';
import { RowActions } from './RowActions';
import { TotalCell } from './txnHelpers';
import { txPriceInDisplay } from '../../utils/displayAmount';
import { usePortfolioContext } from '../../context/PortfolioContext';
import { describeDeletionImpact } from '../../utils/walletTimeline';

/**
 * One transaction as a two-line row: what moved on the left, what it cost on the
 * right, actions on the far right.
 *
 * Shared by the per-coin Buys/Sells columns and by the All Transactions list
 * below `md`, where the eight-column table cannot fit a phone. Extracted rather
 * than copied so the two surfaces cannot drift: they already render the same
 * money through the same <TotalCell>, and the layout should follow.
 *
 * `showAsset` is the only difference between the two. The per-coin view is
 * already grouped by coin AND split into Buys and Sells columns, so naming
 * either on the row would be noise. The All Transactions list mixes both, so it
 * needs the coin icon, the coin name, and a buy/sell indicator.
 */

/** Matches the Buys / Sells / Transfers column headings in CoinTransactionsView. */
const TYPE_DOT: Record<Transaction['type'], string> = {
  BUY: '#6db981',
  SELL: '#c4615e',
  TRANSFER: '#b89968',
};

interface Props {
  tx: Transaction;
  imageMap: Record<string, string>;
  onEdit: (tx: Transaction) => void;
  onDelete: (id: string) => void;
  /** Show the type dot, coin icon and coin name. For lists that mix coins. */
  showAsset?: boolean;
}

export function TxnRow({ tx, imageMap, onEdit, onDelete, showAsset = false }: Props) {
  const { rate, currency } = useCurrency();
  const { transactions } = usePortfolioContext();
  const isSell = tx.type === 'SELL';
  const isTransfer = tx.type === 'TRANSFER';
  // Only used to decide whether a transfer has anything to show; the figure
  // itself is rendered by the shared TotalCell so all the tables agree.
  const total = isTransfer ? (tx.networkFee ?? 0) : tx.amount * tx.price;
  const walletName = isTransfer ? `${tx.fromWallet ?? '—'} → ${tx.toWallet ?? '—'}` : (tx.wallet ?? '');

  const priceOrKind = isTransfer
    ? 'transfer'
    : `@ ${formatExactPrice(txPriceInDisplay(tx, currency.code, rate).value, currency.code)}`;

  return (
    <div
      className="group flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 cursor-pointer transition-colors"
      onClick={() => onEdit(tx)}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgb(var(--overlay) / 0.03)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div className="flex items-center gap-2 min-w-0">
        {showAsset && (
          <>
            {/* Colour alone is not an indicator, so the type is also named for
                screen readers. */}
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: TYPE_DOT[tx.type] }}
            />
            <span className="sr-only">{tx.type}</span>
            <CoinIcon coinId={tx.coinId} symbol={tx.symbol} imageMap={imageMap} size={26} />
          </>
        )}
        <div className="min-w-0">
          <p className="text-[12px] font-medium num" style={{ color: 'rgb(var(--sl-100))' }}>
            {formatAmount(tx.amount)} <span style={{ color: 'var(--color-sub)' }}>{tx.symbol}</span>
          </p>
          {/* The per-coin row truncates: it is one line in a narrow column and
              everything on it is already known from context. The card variant
              wraps instead, because the asset name pushes the DATE past the edge
              at 375px and a transaction you cannot date is not much use. */}
          <p className={`text-[10px] mt-0.5 ${showAsset ? '' : 'truncate'}`} style={{ color: 'var(--color-sub)' }}>
            {showAsset && <>{tx.name} · </>}
            {priceOrKind} · {formatDateShort(tx.date)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <div className="text-right">
          <p className="text-[12px] font-medium num" style={{ color: isSell ? 'var(--color-positive)' : 'rgb(var(--sl-100))' }}>
            {isTransfer && total === 0 ? '—' : <TotalCell tx={tx} />}
          </p>
          {walletName && (
            <p className="text-[10px] mt-0.5 truncate max-w-[110px]" style={{ color: 'var(--color-sub)' }}>{walletName}</p>
          )}
        </div>
        <RowActions
          onEdit={() => onEdit(tx)}
          onDelete={() => onDelete(tx.id)}
          deleteWarning={() => describeDeletionImpact(transactions, tx.id)}
        />
      </div>
    </div>
  );
}
